package org.zq.linkgame.ui.model

/**
 * Game difficulty levels
 * @param displayName 显示名称
 * @param rows 行数
 * @param cols 列数
 * @param levelsToPass 成功后过关数
 * @param patternTypes 图案种类数
 */
enum class Difficulty(
    val displayName: String,
    val rows: Int,
    val cols: Int,
    val levelsToPass: Int,
    val patternTypes: Int
) {
    EASY("简单", 6, 12, 1, 22),        // 6x12 = 72格, 过1关, 22种图案
    HARD("困难", 8, 14, 2, 24),       // 8x14 = 112格, 过2关, 24种图案
    EXPERT("复杂", 10, 16, 3, 26),    // 10x16 = 160格, 过3关, 26种图案
    MASTER("最难", 14, 20, 5, 30)     // 14x20 = 280格, 过5关, 30种图案
}

/**
 * Represents a level pack category
 */
enum class LevelCategory(val displayName: String) {
    ANIMAL("动物乐园"),
    FOOD("美食天地"),
    FAIRY_TALE("奇幻童话")
}

/**
 * Represents a level pack containing multiple levels
 */
data class LevelPack(
    val id: Int,
    val name: String,
    val category: LevelCategory,
    val levelCount: Int,
    val isUnlocked: Boolean = false
)

/**
 * Represents a single game level
 */
data class Level(
    val id: Int,
    val name: String,
    val rows: Int = 8, // Number of rows
    val cols: Int = 8, // Number of columns
    val theme: String,
    val packId: Int
) {
    @Deprecated("Use rows and cols instead", ReplaceWith("rows"))
    val gridSize: Int get() = rows
}

/**
 * Represents a cell position in the game grid
 */
data class CellPosition(
    val row: Int,
    val col: Int
)

/**
 * Represents a game tile/pattern in the grid
 */
data class GameTile(
    val id: Int,
    val patternType: Int, // Index of the pattern type
    val position: CellPosition,
    val isMatched: Boolean = false
)

/**
 * Game props/power-ups
 */
enum class GameProp(val displayName: String, val description: String) {
    HINT("提示", "显示一对可消除的图案"),
    BOMB("炸弹", "消除选中的图案"),
    SHUFFLE("洗牌", "重新排列所有图案")
}

/**
 * Represents the current game state
 */
data class GameState(
    val levelId: Int,
    val levelName: String,
    val currentLevelIndex: Int,
    val totalLevels: Int,
    val rows: Int,    // Number of rows in grid
    val cols: Int,    // Number of columns in grid
    val difficulty: Difficulty = Difficulty.EASY, // Game difficulty
    val tiles: List<GameTile>,
    val selectedTiles: List<CellPosition> = emptyList(),
    val matchedPairs: Int = 0,
    val totalPairs: Int = 0,
    val props: Map<GameProp, Int> = mapOf(
        GameProp.HINT to 3,
        GameProp.BOMB to 2,
        GameProp.SHUFFLE to 1
    ),
    val score: Int = 0,
    val isGameComplete: Boolean = false
) {
    // Backward compatibility
    @Deprecated("Use rows and cols instead")
    val gridSize: Int get() = rows
    
    // 成功后可以过的关卡数
    val levelsToPass: Int get() = difficulty.levelsToPass
}

/**
 * Navigation routes
 */
sealed class Screen(val route: String) {
    data object Main : Screen("main")
    data object LevelSelect : Screen("level_select")
    data object Game : Screen("game/{levelId}/{levelName}/{currentIndex}/{totalLevels}") {
        fun createRoute(levelId: Int, levelName: String, currentIndex: Int, totalLevels: Int): String {
            return "game/$levelId/$levelName/$currentIndex/$totalLevels"
        }
    }
}

/**
 * Sample data for development
 */
object SampleData {
    val levelPacks = listOf(
        LevelPack(1, "可爱小动物", LevelCategory.ANIMAL, 15, true),
        LevelPack(2, "海洋世界", LevelCategory.ANIMAL, 12, true),
        LevelPack(3, "森林奇遇", LevelCategory.ANIMAL, 10, true),
        LevelPack(4, "美味甜点", LevelCategory.FOOD, 15, true),
        LevelPack(5, "水果派对", LevelCategory.FOOD, 12, true),
        LevelPack(6, "中华美食", LevelCategory.FOOD, 18, true),
        LevelPack(7, "童话城堡", LevelCategory.FAIRY_TALE, 15, true),
        LevelPack(8, "魔法森林", LevelCategory.FAIRY_TALE, 12, true),
        LevelPack(9, "星际冒险", LevelCategory.FAIRY_TALE, 20, true)
    )
    
    fun getLevelPacksByCategory(category: LevelCategory): List<LevelPack> {
        return levelPacks.filter { it.category == category }
    }
    
    fun createSampleGameState(
        levelId: Int = 5,
        difficulty: Difficulty = Difficulty.EASY
    ): GameState {
        val rows = difficulty.rows
        val cols = difficulty.cols
        val tiles = mutableListOf<GameTile>()
        var tileId = 0
        val totalCells = rows * cols
        
        // Ensure total cells is even for proper pairing
        // If odd, we'll use one less cell (leave one position empty)
        val usableCells = if (totalCells % 2 == 0) totalCells else totalCells - 1
        val patternCount = usableCells / 2
        
        // Create pairs of patterns - each pattern appears exactly twice
        for (i in 0 until patternCount) {
            val patternType = i % difficulty.patternTypes // pattern types based on difficulty
            // Add exactly 2 tiles with the same pattern
            repeat(2) {
                tiles.add(
                    GameTile(
                        id = tileId++,
                        patternType = patternType,
                        position = CellPosition(0, 0), // Will be assigned later
                        isMatched = false
                    )
                )
            }
        }
        
        // Shuffle and assign positions
        val shuffledTiles = tiles.shuffled().mapIndexed { index, tile ->
            tile.copy(
                position = CellPosition(
                    row = index / cols,
                    col = index % cols
                )
            )
        }
        
        return GameState(
            levelId = levelId,
            levelName = difficulty.displayName,
            currentLevelIndex = 1,
            totalLevels = Int.MAX_VALUE, // 无限关卡
            rows = rows,
            cols = cols,
            difficulty = difficulty,
            tiles = shuffledTiles,
            totalPairs = patternCount
        )
    }
}

/**
 * Background theme options
 */
enum class BackgroundTheme(
    val displayName: String,
    val topColor: Long,
    val bottomColor: Long
) {
    SKY("蓝天", 0xFFBBDEFB, 0xFFF5FAFF),
    SUNSET("晚霞", 0xFFFFCCBC, 0xFFFFF3E0),
    FOREST("森林", 0xFFA5D6A7, 0xFFE8F5E9),
    LAVENDER("薰衣草", 0xFFE1BEE7, 0xFFF3E5F5),
    OCEAN("海洋", 0xFF80DEEA, 0xFFE0F7FA),
    WARM("暖阳", 0xFFFFE0B2, 0xFFFFF8E1),
    CHERRY("樱花", 0xFFF8BBD9, 0xFFFCE4EC),
    MINT("薄荷", 0xFFB2DFDB, 0xFFE0F2F1),
    PEACH("蜜桃", 0xFFFFAB91, 0xFFFBE9E7),
    NIGHT("星空", 0xFF7986CB, 0xFFC5CAE9),
    LEMON("柠檬", 0xFFFFF59D, 0xFFFFFDE7),
    ROSE("玫瑰", 0xFFEF9A9A, 0xFFFFEBEE),
    SNOW("雪地", 0xFFECEFF1, 0xFFFAFAFA),
    CORAL("珊瑚", 0xFFFF8A80, 0xFFFFCDD2),
    SAGE("鼠尾草", 0xFFC5E1A5, 0xFFF1F8E9),
    CLOUD("云彩", 0xFFB3E5FC, 0xFFE1F5FE),
    GRAPE("葡萄", 0xFFCE93D8, 0xFFF3E5F5),
    HONEY("蜂蜜", 0xFFFFD54F, 0xFFFFF8E1),
    TEAL("青碧", 0xFF80CBC4, 0xFFE0F2F1),
    CREAM("奶油", 0xFFFFE4B5, 0xFFFFF9F0),
    INK("墨夜", 0xFF263238, 0xFF455A64),
    ABYSS("深渊", 0xFF1A237E, 0xFF3949AB),
    WINE("酒红", 0xFF4E342E, 0xFF8D6E63),
    JUNGLE("丛林", 0xFF1B5E20, 0xFF4CAF50),
    PURE_BLACK("纯黑", 0xFF000000, 0xFF000000),
    CHARCOAL("炭灰", 0xFF37474F, 0xFF607D8B),
    MIDNIGHT("午夜蓝", 0xFF0D47A1, 0xFF1976D2),
    PLUM("深紫", 0xFF4A148C, 0xFF7B1FA2);
    
    /**
     * Check if this is a dark theme based on top color brightness
     */
    val isDark: Boolean
        get() {
            val r = ((topColor shr 16) and 0xFF) / 255f
            val g = ((topColor shr 8) and 0xFF) / 255f
            val b = (topColor and 0xFF) / 255f
            val brightness = (r * 299 + g * 587 + b * 114) / 1000
            return brightness < 0.5f
        }
    
    /**
     * Get contrasting text color for this theme
     * Returns white for dark backgrounds, deep blue for light backgrounds
     */
    val textColor: Long
        get() = if (isDark) 0xFFFFFFFF else 0xFF1565C0 // White or DeepBlue
    
    /**
     * Get secondary text color for this theme  
     */
    val secondaryTextColor: Long
        get() = if (isDark) 0xFFB0BEC5 else 0xFF546E7A // Light gray or DarkGray
}

/**
 * Supported languages for the app
 */
enum class AppLanguage(
    val displayName: String,
    val nativeName: String,
    val code: String
) {
    SYSTEM("跟随系统", "System", "system"),
    CHINESE("中文", "中文", "zh"),
    ENGLISH("English", "English", "en"),
    FRENCH("Français", "Français", "fr"),
    GERMAN("Deutsch", "Deutsch", "de"),
    JAPANESE("日本語", "日本語", "ja"),
    SPANISH("Español", "Español", "es"),
    KOREAN("한국어", "한국어", "ko"),
    RUSSIAN("Русский", "Русский", "ru"),
    PORTUGUESE("Português", "Português", "pt"),
    ITALIAN("Italiano", "Italiano", "it"),
    ARABIC("العربية", "العربية", "ar"),
    THAI("ไทย", "ไทย", "th"),
    VIETNAMESE("Tiếng Việt", "Tiếng Việt", "vi"),
    INDONESIAN("Indonesia", "Indonesia", "id"),
    TURKISH("Türkçe", "Türkçe", "tr"),
    DUTCH("Nederlands", "Nederlands", "nl"),
    POLISH("Polski", "Polski", "pl"),
    GREEK("Ελληνικά", "Ελληνικά", "el"),
    HINDI("हिन्दी", "हिन्दी", "hi")
}
